import { useCallback, useMemo, useRef, useState } from 'react'
import type { FocusEvent, KeyboardEvent } from 'react'
import { Link } from 'react-router-dom'
import logoLt from '../../assets/logo-lt.svg'
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/solid'
import { useMatchMedia } from '../../hooks'
import {
    EXPERTISE_NAV_PATH_ID,
    NAV_COLLAPSE_ID,
    NAV_MENU_TRIGGER_ID,
} from './nav.constants'
import { NavbarExpertisesBlock } from './NavbarExpertisesBlock'
import { useExpertisesDropdown } from './useExpertisesDropdown'
import { useNavbarNavData } from './useNavbarNavData'

export const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false)
    const burgerButtonRef = useRef<HTMLButtonElement>(null)

    const isDesktopMenubar = useMatchMedia('(min-width: 1024px)', true)
    const { loading, paths, expertisePages } = useNavbarNavData()

    const menubarItemsCount = paths.length

    const expertiseMenubarIndex = useMemo(
        () => paths.findIndex((p) => p.id === EXPERTISE_NAV_PATH_ID) ?? -1,
        [paths]
    )

    const focusMenubarItemAt = useCallback(
        (index: number) => {
            if (menubarItemsCount <= 0) return
            const wrapped =
                ((index % menubarItemsCount) + menubarItemsCount) %
                menubarItemsCount
            const el = document.querySelector<HTMLElement>(
                `#${NAV_COLLAPSE_ID} [data-menubar-index="${wrapped}"]`
            )
            el?.focus()
        },
        [menubarItemsCount]
    )

    const expertisesDropdown = useExpertisesDropdown({
        menubarItemsCount,
        expertiseMenubarIndex,
        isDesktopMenubar,
        focusMenubarItemAt,
    })

    const handleMenubarLinkKeyDown = useCallback(
        (e: KeyboardEvent<HTMLAnchorElement>, idx: number) => {
            const count = menubarItemsCount
            if (count <= 0) return

            const nextKey = isDesktopMenubar ? 'ArrowRight' : 'ArrowDown'
            const prevKey = isDesktopMenubar ? 'ArrowLeft' : 'ArrowUp'

            if (e.key === nextKey) {
                e.preventDefault()
                focusMenubarItemAt(idx + 1)
            } else if (e.key === prevKey) {
                e.preventDefault()
                focusMenubarItemAt(idx - 1)
            } else if (e.key === 'Home') {
                e.preventDefault()
                focusMenubarItemAt(0)
            } else if (e.key === 'End') {
                e.preventDefault()
                focusMenubarItemAt(count - 1)
            }
        },
        [menubarItemsCount, isDesktopMenubar, focusMenubarItemAt]
    )

    const toggleNavbar = () => {
        setIsOpen((prev) => {
            if (prev) {
                expertisesDropdown.setDropdownOpen(false)
                queueMicrotask(() => burgerButtonRef.current?.focus())
            }
            return !prev
        })
    }

    const closeMobileMenu = useCallback(() => {
        setIsOpen(false)
        expertisesDropdown.setDropdownOpen(false)
    }, [expertisesDropdown.setDropdownOpen])

    const handleMobileNavFocusOut = useCallback(
        (e: FocusEvent<HTMLElement>) => {
            if (isDesktopMenubar || !isOpen) return

            const next = e.relatedTarget as Node | null
            const nav = e.currentTarget
            const burger = burgerButtonRef.current

            if (next && (nav.contains(next) || burger?.contains(next))) return

            closeMobileMenu()
        },
        [isDesktopMenubar, isOpen, closeMobileMenu]
    )

    const handleNavKeyDown = (e: KeyboardEvent<HTMLElement>) => {
        if (
            e.key === 'Escape' &&
            isOpen &&
            typeof window !== 'undefined' &&
            window.innerWidth < 1024
        ) {
            e.preventDefault()
            closeMobileMenu()
            burgerButtonRef.current?.focus()
        }
    }

    const navLinkClass = (active: boolean) =>
        `rounded-full py-2 px-4 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2 focus-visible:rounded-sm ${
            active ? 'force-hover' : ''
        }`

    const navItemStyle = {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: '1.4',
    } as const

    return (
        <header
            className="bg-white w-full sticky top-0 z-50"
            role="banner"
            onKeyDown={handleNavKeyDown}
        >
            <div className="container bg-white z-50 px-4 md:px-[6rem] max-w-full flex flex-wrap justify-between items-center py-2 md:py-6">
                <a
                    href="/"
                    lang="en"
                    className="flex items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2 focus-visible:rounded-sm"
                >
                    <span className="sr-only">Retour à l’accueil du site </span>
                    <img
                        src={logoLt}
                        className="mr-1 h-6 sm:h-9 self-center"
                        alt=""
                    />
                    <p
                        className="self-center text-xl font-semibold whitespace-nowrap text-black"
                        lang="en"
                    >
                        Listen too
                    </p>
                </a>
                <h2 className="lg:hidden m-0">
                    <button
                        ref={burgerButtonRef}
                        id={NAV_MENU_TRIGGER_ID}
                        type="button"
                        className="inline-flex items-center p-2 ml-3 text-sm text-black-500 rounded-lg hover:bg-gray-100 bg-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#0a8335] focus-visible:outline-offset-2 focus-visible:rounded-sm text-gray-400 transition duration-150 ease-in-out"
                        aria-label={
                            isOpen
                                ? 'Fermer le menu principal'
                                : 'Ouvrir le menu principal'
                        }
                        aria-controls={NAV_COLLAPSE_ID}
                        aria-expanded={isOpen ? 'true' : 'false'}
                        onClick={toggleNavbar}
                    >
                        <span aria-hidden="true">Menu</span>
                        {isOpen ? (
                            <XMarkIcon
                                className="w-6 h-6 ml-2"
                                aria-hidden="true"
                            />
                        ) : (
                            <Bars3Icon
                                className="w-6 h-6 ml-2"
                                aria-hidden="true"
                            />
                        )}
                    </button>
                </h2>
                <nav
                    className={`${
                        isOpen ? 'block' : 'hidden'
                    } w-full lg:block lg:w-auto`}
                    id={NAV_COLLAPSE_ID}
                    hidden={!isDesktopMenubar && !isOpen ? true : undefined}
                    role={isDesktopMenubar ? 'navigation' : 'region'}
                    aria-label={isDesktopMenubar ? 'Menu principal' : undefined}
                    aria-labelledby={
                        isDesktopMenubar ? undefined : NAV_MENU_TRIGGER_ID
                    }
                    onBlur={handleMobileNavFocusOut}
                >
                    <ul
                        {...(isDesktopMenubar
                            ? {
                                  role: 'menubar',
                                  'aria-orientation': 'horizontal',
                              }
                            : {})}
                        className="flex flex-col text-left text-xl items-center mt-4 lg:flex-row lg:space-x-8 lg:mt-0 lg:text-sm lg:font-medium"
                    >
                        {loading
                            ? null
                            : paths.map((path, idx) => (
                                  <li
                                      key={path.id}
                                      {...(isDesktopMenubar
                                          ? { role: 'none' }
                                          : {})}
                                      className="relative group"
                                  >
                                      {path.id === EXPERTISE_NAV_PATH_ID ? (
                                          <NavbarExpertisesBlock
                                              menubarIndex={idx}
                                              label={path.attributes.name}
                                              isExpertisesRoute={window.location.pathname.includes(
                                                  '/expertises'
                                              )}
                                              expertisePages={expertisePages}
                                              {...expertisesDropdown}
                                          />
                                      ) : (
                                          <Link
                                              to={`/${path.attributes.url}`}
                                              reloadDocument={[
                                                  '',
                                                  'accueil',
                                                  'nos-metiers',
                                                  'contact',
                                                  'mentions-legales',
                                              ].includes(path.attributes.url)}
                                              {...(isDesktopMenubar
                                                  ? { role: 'menuitem' }
                                                  : {})}
                                              data-menubar-index={idx}
                                              onKeyDown={(e) =>
                                                  handleMenubarLinkKeyDown(
                                                      e,
                                                      idx
                                                  )
                                              }
                                              className={navLinkClass(
                                                  window.location.pathname.includes(
                                                      path.attributes.url
                                                  )
                                              )}
                                              style={navItemStyle}
                                              lang={
                                                  path.attributes.url ===
                                                  'newsroom'
                                                      ? 'en'
                                                      : undefined
                                              }
                                          >
                                              {path.attributes.name}
                                          </Link>
                                      )}
                                  </li>
                              ))}
                    </ul>
                    <div
                        aria-hidden="true"
                        className="block lg:hidden mt-8 border-b w-60"
                        style={{ borderColor: '#dedede' }}
                    />
                </nav>
            </div>
        </header>
    )
}
